// The guarded `localStorage` preference adapter (client-audio design DD9 / spec R7;
// tasks.md 2.2). It is the SOLE durable source of truth for the audio preference and
// lives ENTIRELY OUTSIDE `ClientStoreState` — so the match store's `reset()`
// (`set(initialState)`) never touches it and its contract is unchanged (additive-only,
// review F2). It mirrors `session-store.ts`'s shape for a NEW `localStorage` key
// (`waggle-audio-prefs`): a `StorageLike` shim accepted as a parameter (so production
// passes a `window.localStorage` shim and tests pass an in-memory / throwing double),
// try/catch around every storage call, one authoritative in-memory value hydrated
// synchronously at construction (boot), degrade-to-default on read/malformed/foreign/
// throw, keep the in-memory value on a failed write, and never throw to the caller. A
// tiny module-local subscribable serves the `SoundControl`'s reactive on/off + volume
// render — never the match store (design D7). The stored shape carries a `version` for
// future migration; an unknown/legacy version fails validation and degrades to default.

/** The subset of the DOM `Storage` API the guarded adapter consumes — the same shape as
 * `session-store.ts`'s `SessionStorageLike`, re-declared here for the `localStorage`
 * scope so the adapter accepts either a real `window.localStorage` shim or an in-memory
 * test double. */
export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The durable audio preference (spec R7). `enabled` is the two-axis lifecycle's durable
 * axis; `masterVolume` is the master-gain level (0..1); `version` is the stored-shape
 * version for future migration. */
export interface AudioPrefs {
  enabled: boolean;
  masterVolume: number;
  version: number;
}

/** The `localStorage` key the adapter owns. Distinct from the design-locked
 * `waggle-session` `sessionStorage` record (wrong scope, strict validator — do not
 * extend it). */
export const AUDIO_PREFS_KEY = 'waggle-audio-prefs';

/** The current stored-shape version. A stored value carrying any other version is treated
 * as foreign/legacy and degrades to the safe default until a migration is written. */
export const AUDIO_PREFS_VERSION = 1;

/** The fixed default master-gain level used before the player ever touches the control
 * and whenever a read degrades to the safe default. */
export const DEFAULT_MASTER_VOLUME = 0.8;

/** The safe default preference: audio is off until an explicit first-gesture unlock
 * (autoplay policy, spec R6), at a fixed default volume. */
export const DEFAULT_AUDIO_PREFS: AudioPrefs = {
  enabled: false,
  masterVolume: DEFAULT_MASTER_VOLUME,
  version: AUDIO_PREFS_VERSION,
};

/** A subscriber notified with the new authoritative value whenever it changes. */
export type AudioPrefsListener = (prefs: AudioPrefs) => void;

/** The patch a caller applies via `set()` — only the mutable preference fields; `version`
 * is always written as the current `AUDIO_PREFS_VERSION`. */
export type AudioPrefsPatch = Partial<Pick<AudioPrefs, 'enabled' | 'masterVolume'>>;

/** The guarded preference adapter (design DD9). `get()` returns the authoritative
 * in-memory value (never re-reads storage after boot); `set()` merges a patch, persists
 * best-effort, and notifies subscribers on an actual change; `subscribe()` is the
 * module-local subscribable for the `SoundControl`'s reactive render. */
export interface AudioPrefsStore {
  /** The authoritative in-memory preference (a fresh copy each call). */
  get(): AudioPrefs;
  /** Merge a patch onto the authoritative value, persist best-effort, and notify
   * subscribers if the value actually changed. Never throws to the caller. */
  set(patch: AudioPrefsPatch): void;
  /** Subscribe to value changes; returns an unsubscribe. */
  subscribe(listener: AudioPrefsListener): () => void;
}

/**
 * Runtime-validates an untrusted parsed value against the `AudioPrefs` shape, returning a
 * fresh well-typed record on success or `null` on ANY failure (mirroring
 * `validatePersistedSession`). A stored `null`, an array, a wrong-typed field, an
 * out-of-range `masterVolume`, a missing/foreign `version`, or a legacy shape all return
 * `null` — which is what lets a malformed/foreign/legacy value degrade to the safe
 * default rather than installing a bad preference.
 */
export function validateAudioPrefs(value: unknown): AudioPrefs | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.enabled !== 'boolean') {
    return null;
  }
  if (
    typeof record.masterVolume !== 'number' ||
    !Number.isFinite(record.masterVolume) ||
    record.masterVolume < 0 ||
    record.masterVolume > 1
  ) {
    return null;
  }
  if (record.version !== AUDIO_PREFS_VERSION) {
    return null;
  }
  return {
    enabled: record.enabled,
    masterVolume: record.masterVolume,
    version: AUDIO_PREFS_VERSION,
  };
}

/** Reads and validates the stored preference, degrading to the safe default on a missing
 * value, malformed JSON, a validation failure, or a throwing backend — without clearing
 * any key (unrelated or otherwise), so hydration never disturbs storage. */
function hydrate(storage: StorageLike, key: string): AudioPrefs {
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return { ...DEFAULT_AUDIO_PREFS }; // storage unavailable — safe default
  }
  if (raw === null) {
    return { ...DEFAULT_AUDIO_PREFS };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_AUDIO_PREFS }; // malformed JSON — safe default
  }
  const validated = validateAudioPrefs(parsed);
  return validated ?? { ...DEFAULT_AUDIO_PREFS };
}

/**
 * Builds an `AudioPrefsStore` over a `Storage`-like backing (design DD9). The
 * authoritative in-memory value is hydrated **synchronously here at construction** (boot,
 * before the driver is wired), so `get()` never re-reads storage. `set()` merges a patch,
 * writes the JSON-serialized `{ enabled, masterVolume, version }` best-effort (a throwing
 * write keeps the in-memory value and does not throw), and notifies subscribers only on an
 * actual change.
 */
export function createAudioPrefsStore(
  storage: StorageLike,
  key: string = AUDIO_PREFS_KEY,
): AudioPrefsStore {
  let current: AudioPrefs = hydrate(storage, key);
  const listeners = new Set<AudioPrefsListener>();

  const persist = (): void => {
    try {
      storage.setItem(key, JSON.stringify(current));
    } catch {
      // Storage disabled/full/sandboxed: the preference just does not survive a reload;
      // the authoritative in-memory value is unaffected.
    }
  };

  return {
    get(): AudioPrefs {
      return { ...current };
    },
    set(patch: AudioPrefsPatch): void {
      const next: AudioPrefs = {
        enabled: patch.enabled ?? current.enabled,
        masterVolume: patch.masterVolume ?? current.masterVolume,
        version: AUDIO_PREFS_VERSION,
      };
      const changed =
        next.enabled !== current.enabled || next.masterVolume !== current.masterVolume;
      current = next;
      // Always attempt the write (so a previously-failed write can succeed on a later
      // set), but only notify subscribers when the observable value actually changed.
      persist();
      if (changed) {
        const snapshot = { ...current };
        for (const listener of listeners) {
          listener(snapshot);
        }
      }
    },
    subscribe(listener: AudioPrefsListener): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
